<!-- src/components/ChatDisplay.vue -->
<template>
  <div class="chat-display">
    <div v-if="!answerLoading && conversations.length == 0">
      <h2>Welcome</h2>
      <InitialChatSuggestions />
    </div>
    <div
      class="chat-item"
      v-for="(conversation, index) in conversations"
      :key="index"
    >
      <div class="header-bubble">You</div>
      <p class="question" ref="questions">{{ conversation.question }}</p>
      <div class="header-bubble">Answer AI</div>
      <div v-if="answerLoading && index === conversations.length - 1">
        <div class="skeleton" aria-busy="true" aria-live="polite">
          <div
            class="skeleton-paragraph"
            role="status"
            aria-label="Loading content..."
          ></div>
          <div
            class="skeleton-paragraph"
            role="status"
            aria-label="Loading content..."
          ></div>
          <div
            class="skeleton-paragraph"
            role="status"
            aria-label="Loading content..."
          ></div>
          <div
            class="skeleton-paragraph"
            role="status"
            aria-label="Loading content..."
          ></div>
        </div>
      </div>
      <div v-else class="answer">
        <div v-if="conversation.outcome !== 'success'" class="answer">
          <p v-if="conversation.outcome === 'content_filter'">
            Sorry, I am not able to respond to that question.
          </p>
          <p v-else-if="conversation.outcome === 'Rate limit exceeded'">
            Sorry, I am not able to respond right now. Please try again.
          </p>
          <p v-else>
            Sorry, I am not able to respond to that question. Please try again.
          </p>
        </div>

        <div v-else>
          <div
            v-html="
              renderAnswer(
                conversation.answer ?? '',
                conversation.sources ?? []
              )
            "
          ></div>
          <div class="ai-warning">
            AI-generated content may contain mistakes.
          </div>
          <Sources :sources="conversation.sources" />
          <Feedback
            :sessionId="sessionId"
            :interactionId="conversation.interaction_id"
          />
          <SuggestedFollowOnQuestions
            :followOnQuestions="conversation.suggestedFollowOnQuestions"
            :suggestionsLoading="suggestionsLoading"
            v-if="view !== 'uat' && index === conversations.length - 1"
          />
        </div>
      </div>
    </div>
    <div v-if="error">{{ error }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onUpdated } from 'vue';
import { useAppStore } from '@/store/appStore';
import { useQuestionStore } from '../store/questionStore';
import { renderAnswer } from '../utils/display';
import InitialChatSuggestions from './InitialChatSuggestions.vue';
import Sources from './Sources.vue';
import Feedback from './Feedback.vue';
import SuggestedFollowOnQuestions from './SuggestedFollowOnQuestions.vue';

export default defineComponent({
  components: {
    InitialChatSuggestions,
    Sources,
    Feedback,
    SuggestedFollowOnQuestions
  },
  setup() {
    const { view } = useAppStore();
    const questionStore = useQuestionStore();
    const questions = ref<HTMLElement[]>([]);
    const conversations = questionStore.getConversations;
    const sessionId = questionStore.getSessionId;
    const answerLoading = computed(() => questionStore.answerLoading);
    const suggestionsLoading = computed(() => questionStore.suggestionsLoading);
    const error = computed(() => questionStore.error);
    onUpdated(() => {
      if (questions.value) {
        const lastQuestionElement = questions.value[questions.value.length - 1];
        if (lastQuestionElement) {
          lastQuestionElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });

    return {
      questions,
      sessionId,
      conversations,
      answerLoading,
      suggestionsLoading,
      error,
      renderAnswer,
      view
    };
  }
});
</script>

<style>
.reference {
  font-weight: bold;
  padding-left: 3px;
}

a {
  text-decoration: underline;
  color: #007bff;
  text-underline-offset: 2px;
}

a.visited {
  color: inherit;
}
</style>

<style scoped>
.chat-display {
  padding: 3px 0px;
  flex: 1;
}

@media (min-width: 530px) {
  .chat-display {
    overflow-y: auto;
  }
}

.header-bubble {
  background-color: #149696;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  border-radius: 7px;
  padding: 1px 12px;
  width: fit-content;
}

.question {
  font-style: italic;
  font-weight: bold;
}

h2 {
  margin-left: auto;
  margin-right: auto;
  margin-top: 60px;
  width: fit-content;
}

h3 {
  margin-top: 0px;
  margin-bottom: -10px;
  font-size: 1.7rem;
  padding-top: 10px;
  color: #650236;
}

h4 {
  margin-top: 5px;
  margin-bottom: 0px;
}

.chat-item {
  background-color: white;
  border-radius: 14px;
  padding: 10px 10px 2px 2px;
  margin-bottom: 10px;
}

.answer {
  line-height: 1.3rem;
}

.ai-warning {
  padding: 2px 4px;
  background-color: #e0e0e0;
  color: black;
  max-width: fit-content;
  font-size: 0.7rem;
  /* line-height: 1.3rem; */
}

.skeleton {
  margin: 10px auto 0 auto;
}

.skeleton-paragraph {
  height: 1rem;
  width: 100%;
  margin: 0.5rem 0;
  background: #d0d0d0;
  border-radius: 5px;
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    background-color: #d0d0d0;
  }

  50% {
    background-color: #f0f0f0;
  }

  100% {
    background-color: #d0d0d0;
  }
}
</style>
